import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Upload, Plus, Edit, Trash2, Eye, FileText, Download, CheckCircle, Image as ImageIcon, Puzzle, ScrollText, MapPin, Users, ChevronDown, ChevronUp, RotateCcw, DollarSign, BarChart3, Scissors, Video, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [uploadingBook, setUploadingBook] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showTierPanel, setShowTierPanel] = useState(false);
  const [tierUsers, setTierUsers] = useState([]);
  const [tierLoading, setTierLoading] = useState(false);
  const [publishingBook, setPublishingBook] = useState(null);
  const [deletingBook, setDeletingBook] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Check admin access
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      // Add token if exists (email/password auth)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/books`, {
        headers: headers,
        credentials: 'include' // Important for cookie-based auth
      });
      
      if (response.status === 403) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setBooks(data.books);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBook = async (formData) => {
    console.log('📘 handleCreateBook called with:', formData);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add token if exists (email/password auth)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('📘 Using JWT token auth');
      } else {
        console.log('📘 Using cookie-based auth (Google OAuth)');
      }
      
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/books`;
      console.log('📘 Sending POST to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        credentials: 'include', // Important for cookie-based auth
        body: JSON.stringify(formData)
      });

      console.log('📘 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Book created successfully:', data);
        await fetchBooks();
        setShowCreateModal(false);
      } else {
        let errorMessage = 'Failed to create book';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          console.error('Failed to parse error response:', e);
        }
        console.error('❌ Error creating book:', errorMessage);
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('❌ Exception creating book:', error);
      alert('Error creating book. Please try again.');
    }
  };

  const handleUploadDocx = async (bookId, file) => {
    try {
      setUploadingBook(bookId);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/books/${bookId}/upload`, {
        method: 'POST',
        headers: headers,
        credentials: 'include', // Important for cookie-based auth
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Success! Uploaded ${data.chapters_count} chapters`);
        await fetchBooks();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file');
    } finally {
      setUploadingBook(null);
    }
  };

  // Auto-clear confirm states after timeout
  useEffect(() => {
    if (confirmPublish) {
      const timer = setTimeout(() => setConfirmPublish(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [confirmPublish]);

  useEffect(() => {
    if (confirmDelete) {
      const timer = setTimeout(() => setConfirmDelete(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [confirmDelete]);

  const handlePublishToggle = async (bookId, currentStatus) => {
    // Two-tap confirmation: first tap sets confirm state, second tap executes
    if (confirmPublish !== bookId) {
      setConfirmPublish(bookId);
      setConfirmDelete(null);
      return;
    }

    setConfirmPublish(null);
    setPublishingBook(bookId);
    const newStatus = currentStatus === 'published' ? 'uploaded' : 'published';

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/books/${bookId}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(newStatus === 'published' ? 'Book published!' : 'Book unpublished');
        await fetchBooks();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(`Failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setPublishingBook(null);
    }
  };

  const handleUploadCover = async (bookId, file) => {
    try {
      setUploadingCover(bookId);
      
      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64Image = reader.result;
        
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/books/${bookId}`, {
          method: 'PUT',
          headers: headers,
          credentials: 'include',
          body: JSON.stringify({ cover_url: base64Image })
        });

        if (response.ok) {
          await fetchBooks();
        } else {
          alert('Failed to upload cover image');
        }
      };
    } catch (error) {
      console.error('Error uploading cover:', error);
      alert('Error uploading cover');
    } finally {
      setUploadingCover(null);
    }
  };

  const handleEditBook = (book) => {
    setSelectedBook(book);
    setShowEditModal(true);
  };

  const handleUpdateBook = async (bookId, updateData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/books/${bookId}`, {
        method: 'PUT',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await fetchBooks();
        setShowEditModal(false);
      } else {
        alert('Failed to update book');
      }
    } catch (error) {
      console.error('Error updating book:', error);
      alert('Error updating book');
    }
  };

  // === TIER MANAGEMENT FUNCTIONS ===
  const fetchTierUsers = async () => {
    setTierLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tiers/users`, {
        headers, credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setTierUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch tier users:', err);
    }
    setTierLoading(false);
  };

  const handleChangeTier = async (email, newTier, resetProgress = false) => {
    const action = resetProgress
      ? `Change ${email} to ${newTier} and RESET all progress?`
      : `Change ${email} to ${newTier} tier?`;
    if (!window.confirm(action)) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tiers/change`, {
        method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify({ email, tier: newTier, reset_progress: resetProgress }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${email} changed to ${data.new_tier}${data.progress_reset ? ' (progress reset)' : ''}`);
        fetchTierUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to change tier');
      }
    } catch (err) {
      toast.error('Error changing tier');
    }
  };

  const handleResetProgress = async (email) => {
    if (!window.confirm(`Reset ALL puzzle and reading progress for ${email}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tiers/reset-progress`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ email, tier: 'seeker' }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Progress reset for ${email}: ${data.puzzle_records_deleted} puzzle, ${data.reading_records_deleted} reading records deleted`);
      } else {
        toast.error('Failed to reset progress');
      }
    } catch (err) {
      toast.error('Error resetting progress');
    }
  };

  const handleDeleteBook = async (bookId) => {
    // Two-tap confirmation
    if (confirmDelete !== bookId) {
      setConfirmDelete(bookId);
      setConfirmPublish(null);
      return;
    }

    setConfirmDelete(null);
    setDeletingBook(bookId);

    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/books/${bookId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Book deleted successfully');
        await fetchBooks();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(`Failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setDeletingBook(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have admin permissions.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-8 border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">📚 Book Publishing Admin</h1>
              <p className="text-gray-300">Digital Publishing Platform - Trump of the Lord Ministries</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate('/admin/appendices')}
                variant="outline"
                className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                data-testid="admin-appendices-btn"
              >
                <ScrollText className="h-4 w-4 mr-2" />
                Content Manager
              </Button>
              <Button
                onClick={() => navigate('/admin/puzzle')}
                variant="outline"
                className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                data-testid="admin-puzzle-btn"
              >
                <Puzzle className="h-4 w-4 mr-2" />
                Puzzle Editor
              </Button>
              <Button
                onClick={() => navigate('/admin/escort-guard')}
                variant="outline"
                className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                data-testid="admin-escort-guard-btn"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Escort Guard
              </Button>
              <Button
                onClick={() => { setShowTierPanel(!showTierPanel); if (!showTierPanel && tierUsers.length === 0) fetchTierUsers(); }}
                variant="outline"
                className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
                data-testid="admin-tier-mgmt-btn"
              >
                <Users className="h-4 w-4 mr-2" />
                Tier Testing
              </Button>
              <Button
                onClick={() => navigate('/admin/pricing')}
                variant="outline"
                className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                data-testid="admin-pricing-btn"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Pricing
              </Button>
              <Button
                onClick={() => navigate('/admin/site-config')}
                variant="outline"
                className="border-violet-600 text-violet-400 hover:bg-violet-900/30"
                data-testid="admin-site-config-btn"
              >
                <Edit className="h-4 w-4 mr-2" />
                Content Manager
              </Button>
              <Button
                onClick={() => navigate('/admin/analytics')}
                variant="outline"
                className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
                data-testid="admin-analytics-btn"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </Button>
              <Button
                onClick={() => navigate('/admin/podcast')}
                variant="outline"
                className="border-rose-600 text-rose-400 hover:bg-rose-900/30"
                data-testid="admin-podcast-btn"
              >
                <Video className="h-4 w-4 mr-2" />
                Podcast Studio
              </Button>
              <Button
                onClick={() => navigate('/admin/lessons')}
                variant="outline"
                className="border-teal-600 text-teal-400 hover:bg-teal-900/30"
                data-testid="admin-lessons-btn"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Lesson Editor
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="new-book-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Book
              </Button>
              <Button
                onClick={() => setShowSplitModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="split-appendices-btn"
              >
                <Scissors className="h-4 w-4 mr-2" />
                Split Appendices
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tier Management Panel */}
      {showTierPanel && (
        <div className="bg-slate-900 border-b border-slate-700" data-testid="tier-management-panel">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-cyan-400" />
                  Subscription Tier Testing
                </h2>
                <p className="text-sm text-slate-200">Switch tiers to test what each access level sees. Reset progress to test the journey fresh.</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowTierPanel(false)} className="text-slate-200 hover:text-white">
                <ChevronUp className="h-5 w-5" />
              </Button>
            </div>

            {tierLoading ? (
              <p className="text-slate-200 text-sm">Loading users...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-slate-200 font-medium">User</th>
                      <th className="text-left py-2 px-3 text-slate-200 font-medium">Current Tier</th>
                      <th className="text-left py-2 px-3 text-slate-200 font-medium">Change Tier</th>
                      <th className="text-left py-2 px-3 text-slate-200 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tierUsers.map(u => (
                      <tr key={u.email} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="py-3 px-3">
                          <p className="text-white font-medium">{u.name || 'Unknown'}</p>
                          <p className="text-slate-300 text-xs">{u.email}</p>
                        </td>
                        <td className="py-3 px-3">
                          <Badge data-testid={`tier-badge-${u.email}`} className={
                            u.subscription_tier === 'scholar' ? 'bg-purple-600 text-white' :
                            u.subscription_tier === 'berean' ? 'bg-blue-600 text-white' :
                            u.subscription_tier === 'church' ? 'bg-emerald-600 text-white' :
                            'bg-amber-600 text-white'
                          }>
                            {u.subscription_tier === 'church' ? 'Pastor/Church' : (u.subscription_tier || 'seeker')}
                          </Badge>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1 flex-wrap">
                            {['seeker', 'berean', 'scholar', 'church'].map(tier => {
                              const displayName = tier === 'church' ? 'Pastor/Church' : tier;
                              return (
                              <button
                                key={tier}
                                data-testid={`set-tier-${tier}-${u.email}`}
                                onClick={() => handleChangeTier(u.email, tier)}
                                disabled={u.subscription_tier === tier}
                                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                  u.subscription_tier === tier
                                    ? 'bg-slate-700 text-slate-300 cursor-not-allowed'
                                    : tier === 'scholar' ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-800/50 border border-purple-700/50'
                                    : tier === 'berean' ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800/50 border border-blue-700/50'
                                    : tier === 'church' ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800/50 border border-emerald-700/50'
                                    : 'bg-amber-900/50 text-amber-300 hover:bg-amber-800/50 border border-amber-700/50'
                                }`}
                              >
                                {displayName}
                              </button>
                            )})}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-2">
                            <button
                              data-testid={`reset-progress-${u.email}`}
                              onClick={() => handleResetProgress(u.email)}
                              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-red-900/30 text-red-400 hover:bg-red-800/40 border border-red-700/30 transition-colors"
                              title="Reset all puzzle + reading progress"
                            >
                              <RotateCcw className="h-3 w-3" />
                              Reset Progress
                            </button>
                            <button
                              data-testid={`reset-to-new-${u.email}`}
                              onClick={() => handleChangeTier(u.email, 'seeker', true)}
                              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-orange-900/30 text-orange-400 hover:bg-orange-800/40 border border-orange-700/30 transition-colors"
                              title="Set to Seeker and wipe all progress"
                            >
                              <RotateCcw className="h-3 w-3" />
                              Reset to New
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {books.length === 0 ? (
          <Card className="p-12 text-center">
            <Book className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No Books Yet</h2>
            <p className="text-gray-600 mb-6">
              Create your first book to get started with digital publishing
            </p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="h-4 w-4 mr-2" />
              Create First Book
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {books.map((book) => (
              <Card key={book.book_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Book Cover */}
                <div className="relative h-48 bg-gradient-to-br from-amber-100 to-amber-50">
                  {book.cover_url ? (
                    <img 
                      src={book.cover_url} 
                      alt={book.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Book className="h-20 w-20 text-amber-300" />
                    </div>
                  )}
                  <Badge className={
                    book.status === 'published' ? 'absolute top-2 right-2 bg-green-500' :
                    book.status === 'uploaded' ? 'absolute top-2 right-2 bg-blue-500' :
                    'absolute top-2 right-2 bg-gray-500'
                  }>
                    {book.status}
                  </Badge>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="font-bold text-lg mb-1">{book.title}</h3>
                    {book.subtitle && (
                      <p className="text-sm text-gray-600 mb-2">{book.subtitle}</p>
                    )}
                    <p className="text-sm text-gray-500">by {book.author}</p>
                  </div>

                  {book.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{book.description}</p>
                  )}

                  {book.access_tier && (
                    <p className="text-xs text-amber-600 font-semibold mb-2">
                      {book.access_tier.charAt(0).toUpperCase() + book.access_tier.slice(1)} Tier
                    </p>
                  )}

                  {book.book_type && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        book.book_type === 'main' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                      }`} data-testid={`book-type-badge-${book.book_id}`}>
                        {book.book_type === 'main' ? 'TREASURE MAP' : book.book_type.toUpperCase()}
                      </span>
                      {book.theme && book.book_type === 'main' && (
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          book.theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {book.theme}
                        </span>
                      )}
                    </div>
                  )}

                  {book.chapter_count && (
                    <p className="text-sm text-gray-500 mb-4">
                      📖 {book.chapter_count} chapters
                    </p>
                  )}

                  <div className="space-y-2">
                    {/* Publish/Unpublish Button */}
                    {book.chapter_count > 0 && (
                      <Button
                        type="button"
                        data-testid={`publish-btn-${book.book_id}`}
                        className={
                          confirmPublish === book.book_id
                            ? 'w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 animate-pulse'
                            : book.status === 'published' 
                              ? 'w-full bg-gray-600 hover:bg-gray-700 active:bg-gray-800' 
                              : 'w-full bg-green-600 hover:bg-green-700 active:bg-green-800'
                        }
                        style={{ touchAction: 'manipulation' }}
                        disabled={publishingBook === book.book_id}
                        onClick={() => handlePublishToggle(book.book_id, book.status)}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {publishingBook === book.book_id
                          ? 'Processing...'
                          : confirmPublish === book.book_id
                            ? 'Tap again to confirm'
                            : book.status === 'published' ? 'Unpublish' : 'Publish Book'}
                      </Button>
                    )}

                    {/* Upload Cover */}
                    <div className="block">
                      <input
                        type="file"
                        id={`cover-upload-${book.book_id}`}
                        accept="image/*"
                        className="sr-only"
                        data-testid={`cover-upload-input-${book.book_id}`}
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleUploadCover(book.book_id, e.target.files[0]);
                          }
                          e.target.value = '';
                        }}
                        disabled={uploadingCover === book.book_id}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        data-testid={`cover-upload-btn-${book.book_id}`}
                        disabled={uploadingCover === book.book_id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const input = document.getElementById(`cover-upload-${book.book_id}`);
                          if (input) input.click();
                        }}
                      >
                        <ImageIcon className="h-4 w-4 mr-2" />
                        {uploadingCover === book.book_id ? 'Uploading...' : 'Upload Cover'}
                      </Button>
                    </div>

                    {/* Upload Docx */}
                    <div className="block">
                      <input
                        type="file"
                        id={`docx-upload-${book.book_id}`}
                        className="sr-only"
                        data-testid={`docx-upload-input-${book.book_id}`}
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleUploadDocx(book.book_id, e.target.files[0]);
                          }
                          e.target.value = '';
                        }}
                        disabled={uploadingBook === book.book_id}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        data-testid={`docx-upload-btn-${book.book_id}`}
                        disabled={uploadingBook === book.book_id}
                        onClick={() => {
                          const input = document.getElementById(`docx-upload-${book.book_id}`);
                          if (input) {
                            input.value = '';
                            input.click();
                          }
                        }}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingBook === book.book_id ? 'Uploading...' : 'Upload .docx/.pdf'}
                      </Button>
                    </div>

                    {/* View Chapters */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      data-testid={`view-chapters-btn-${book.book_id}`}
                      style={{ touchAction: 'manipulation' }}
                      onClick={() => navigate(`/admin/books/${book.book_id}`)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Chapters
                    </Button>

                    {/* Edit Book */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      data-testid={`edit-book-btn-${book.book_id}`}
                      style={{ touchAction: 'manipulation' }}
                      onClick={() => handleEditBook(book)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Details
                    </Button>

                    {/* Delete */}
                    <Button
                      type="button"
                      variant="outline"
                      className={
                        confirmDelete === book.book_id
                          ? 'w-full bg-red-600 text-white hover:bg-red-700 active:bg-red-800 animate-pulse'
                          : 'w-full text-red-600 hover:bg-red-50 active:bg-red-100'
                      }
                      data-testid={`delete-book-btn-${book.book_id}`}
                      style={{ touchAction: 'manipulation' }}
                      disabled={deletingBook === book.book_id}
                      onClick={() => handleDeleteBook(book.book_id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {deletingBook === book.book_id
                        ? 'Deleting...'
                        : confirmDelete === book.book_id
                          ? 'Tap again to delete'
                          : 'Delete'}
                    </Button>
                  </div>

                  <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                    Created: {new Date(book.created_at).toLocaleDateString()}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Book Modal */}
      {showCreateModal && (
        <CreateBookModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateBook}
        />
      )}

      {/* Edit Book Modal */}
      {showEditModal && selectedBook && (
        <EditBookModal
          book={selectedBook}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdateBook}
        />
      )}

      {/* Split Appendices Modal */}
      {showSplitModal && (
        <SplitAppendicesModal
          books={books}
          onClose={() => setShowSplitModal(false)}
          onComplete={() => { setShowSplitModal(false); fetchBooks(); }}
        />
      )}

      <Footer />
    </div>
  );
};

// Create Book Modal Component
const CreateBookModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    author: 'Curtis Alexander Fenison ("Mordecai")',
    description: '',
    cover_url: '',
    access_tier: 'seeker'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔵 Form submit triggered');
    console.log('🔵 Form data:', formData);
    
    if (isSubmitting) {
      console.log('⚠️ Already submitting, ignoring duplicate submit');
      return;
    }
    
    setIsSubmitting(true);
    console.log('🔵 Calling onCreate with formData...');
    
    try {
      await onCreate(formData);
      console.log('✅ onCreate completed successfully');
    } catch (error) {
      console.error('❌ onCreate failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = (e) => {
    console.log('🟢 Create Book button clicked (direct click handler)');
    // The form onSubmit will handle the actual submission
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={(e) => {
        // Close modal if clicking backdrop
        if (e.target === e.currentTarget) {
          console.log('🔵 Backdrop clicked, closing modal');
          onClose();
        }
      }}
    >
      <div 
        className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8"
        style={{ position: 'relative', zIndex: 10000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold mb-6">Create New Book</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Book Title *</label>
            <input
              type="text"
              className="w-full border rounded-lg p-3"
              value={formData.title}
              onChange={(e) => {
                console.log('Title changed:', e.target.value);
                setFormData({...formData, title: e.target.value});
              }}
              placeholder="The King's Return"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Subtitle</label>
            <input
              type="text"
              className="w-full border rounded-lg p-3"
              value={formData.subtitle}
              onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
              placeholder="The Legend — Reading the Treasure Map Together"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Author *</label>
            <input
              type="text"
              className="w-full border rounded-lg p-3"
              value={formData.author}
              onChange={(e) => setFormData({...formData, author: e.target.value})}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Description</label>
            <textarea
              className="w-full border rounded-lg p-3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              placeholder="A brief description of the book..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Access Tier *</label>
            <select
              className="w-full border rounded-lg p-3"
              value={formData.access_tier}
              onChange={(e) => setFormData({...formData, access_tier: e.target.value})}
              required
            >
              <option value="seeker">Seeker (Free) - First chapter only</option>
              <option value="berean">Berean (Yearly) - Full access</option>
              <option value="scholar">Scholar - Full library access</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Determines which subscription tier can access this book
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button 
              type="button" 
              onClick={() => {
                console.log('🔵 Cancel button clicked');
                onClose();
              }}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100"
              style={{ touchAction: 'manipulation' }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              onClick={handleButtonClick}
              onTouchEnd={(e) => {
                console.log('🟢 Create Book button touched (iPad/mobile)');
              }}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                position: 'relative',
                zIndex: 10001
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Book'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Book Modal Component
const EditBookModal = ({ book, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: book.title || '',
    subtitle: book.subtitle || '',
    author: book.author || '',
    description: book.description || '',
    access_tier: book.access_tier || 'seeker',
    book_type: book.book_type || 'companion',
    theme: book.theme || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onUpdate(book.book_id, formData);
    } catch (error) {
      console.error('Failed to update book:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8 max-h-[90vh] overflow-y-auto"
        style={{ position: 'relative', zIndex: 10000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold mb-6">Edit Book Details</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Book Title *</label>
            <input
              type="text"
              className="w-full border rounded-lg p-3"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Subtitle</label>
            <input
              type="text"
              className="w-full border rounded-lg p-3"
              value={formData.subtitle}
              onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Author *</label>
            <input
              type="text"
              className="w-full border rounded-lg p-3"
              value={formData.author}
              onChange={(e) => setFormData({...formData, author: e.target.value})}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Description</label>
            <textarea
              className="w-full border rounded-lg p-3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Access Tier *</label>
            <select
              className="w-full border rounded-lg p-3"
              value={formData.access_tier}
              onChange={(e) => setFormData({...formData, access_tier: e.target.value})}
              required
            >
              <option value="seeker">Seeker (Free) - First chapter only</option>
              <option value="berean">Berean (Yearly) - Full access</option>
              <option value="scholar">Scholar - Full library access</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Book Type</label>
              <select
                className="w-full border rounded-lg p-3"
                value={formData.book_type}
                onChange={(e) => setFormData({...formData, book_type: e.target.value})}
                data-testid="edit-book-type"
              >
                <option value="main">Main (Treasure Map Book)</option>
                <option value="companion">Companion</option>
                <option value="appendix">Appendix</option>
              </select>
            </div>
            {formData.book_type === 'main' && (
              <div>
                <label className="block text-sm font-semibold mb-2">Map Theme</label>
                <select
                  className="w-full border rounded-lg p-3"
                  value={formData.theme}
                  onChange={(e) => setFormData({...formData, theme: e.target.value})}
                  data-testid="edit-book-theme"
                >
                  <option value="dark">Dark (The King's Return)</option>
                  <option value="light">Light (Bright/Hopeful)</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Split Appendices Modal Component
const SplitAppendicesModal = ({ books, onClose, onComplete }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [step, setStep] = useState('config'); // 'config' | 'preview' | 'processing' | 'done'
  const [file, setFile] = useState(null);
  const [subtitle, setSubtitle] = useState('Companion Appendices');
  const [price, setPrice] = useState('1.77');
  const [author, setAuthor] = useState('Curtis Alexander Fenison ("Mordecai")');
  const [parentBookId, setParentBookId] = useState('');
  const [generateSummaries, setGenerateSummaries] = useState(true);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (token) return { 'Authorization': `Bearer ${token}` };
    return {};
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/admin/books/split-appendices/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Preview failed');
      }
      const data = await res.json();
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSplit = async () => {
    if (!file) return;
    setStep('processing');
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subtitle', subtitle);
      formData.append('price', price);
      formData.append('author', author);
      if (parentBookId) formData.append('parent_book_id', parentBookId);
      if (generateSummaries) formData.append('generate_summaries', 'true');
      const res = await fetch(`${API}/api/admin/books/split-appendices`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Split failed');
      }
      const data = await res.json();
      setResult(data);
      setStep('done');
      toast.success(`${data.created.length} appendix books created!`);
    } catch (err) {
      setError(err.message);
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="split-appendices-modal"
    >
      <div
        className="max-w-2xl w-full bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-8 max-h-[90vh] overflow-y-auto"
        style={{ position: 'relative', zIndex: 10000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-6">
          <Scissors className="h-6 w-6 text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">Split Appendices</h2>
        </div>
        <p className="text-slate-200 text-sm mb-6">
          Upload a combined companion appendices .docx file. Each appendix (Heading 1) will become its own published book.
        </p>

        {error && (
          <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-3 mb-4 text-red-300 text-sm" data-testid="split-error">
            {error}
          </div>
        )}

        {step === 'config' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Upload Combined .docx *</label>
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-emerald-500/50 transition-colors">
                <input
                  type="file"
                  id="split-file-input"
                  accept="*/*"
                  className="sr-only"
                  data-testid="split-file-input"
                  onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                  data-testid="split-file-btn"
                  onClick={() => document.getElementById('split-file-input').click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {file ? file.name : 'Choose .docx File'}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Copy Cover From (Parent Book)</label>
              <select
                className="w-full border border-slate-600 bg-slate-800 text-white rounded-lg p-3"
                value={parentBookId}
                onChange={(e) => setParentBookId(e.target.value)}
                data-testid="split-parent-book"
              >
                <option value="">None — no cover</option>
                {books.filter(b => b.cover_url).map(b => (
                  <option key={b.book_id} value={b.book_id}>{b.title}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-300">Subtitle</label>
                <input
                  type="text"
                  className="w-full border border-slate-600 bg-slate-800 text-white rounded-lg p-3"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  data-testid="split-subtitle"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-300">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full border border-slate-600 bg-slate-800 text-white rounded-lg p-3"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  data-testid="split-price"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Author</label>
              <input
                type="text"
                className="w-full border border-slate-600 bg-slate-800 text-white rounded-lg p-3"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                data-testid="split-author"
              />
            </div>

            <label className="flex items-center gap-3 p-3 rounded-lg border border-purple-700/30 bg-purple-900/20 cursor-pointer hover:bg-purple-900/30 transition-colors" data-testid="split-ai-toggle">
              <input
                type="checkbox"
                checked={generateSummaries}
                onChange={(e) => setGenerateSummaries(e.target.checked)}
                className="w-4 h-4 rounded border-purple-600 text-purple-600"
              />
              <div>
                <span className="text-sm text-purple-300 font-medium">Generate AI Summaries</span>
                <p className="text-xs text-purple-400/60 mt-0.5">Claude will write readable, laymen-friendly descriptions for each appendix</p>
              </div>
            </label>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1 text-slate-300" onClick={onClose}>Cancel</Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={!file || loading}
                onClick={handlePreview}
                data-testid="split-preview-btn"
              >
                {loading ? 'Scanning...' : 'Scan & Preview'}
              </Button>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg px-4 py-2">
                <span className="text-emerald-300 font-bold">{preview.will_create}</span>
                <span className="text-slate-200 ml-1">new books</span>
              </div>
              <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-4 py-2">
                <span className="text-amber-300 font-bold">{preview.will_skip}</span>
                <span className="text-slate-200 ml-1">will skip</span>
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-1" data-testid="split-preview-list">
              {preview.sections.map((s, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
                  s.will_create ? 'bg-emerald-900/20 text-emerald-300' :
                  s.already_exists ? 'bg-amber-900/20 text-amber-400' :
                  'bg-slate-800 text-slate-300'
                }`}>
                  <span className="font-mono text-xs w-8">{s.appendix_code || '—'}</span>
                  <span className="flex-1 truncate">{s.title}</span>
                  <span className="text-xs whitespace-nowrap">
                    {s.will_create ? 'NEW' : s.already_exists ? 'EXISTS' : 'SKIP'}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1 text-slate-300" onClick={() => setStep('config')}>Back</Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={preview.will_create === 0}
                onClick={handleSplit}
                data-testid="split-confirm-btn"
              >
                Create {preview.will_create} Books
              </Button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="py-12 text-center">
            <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-300">Splitting and publishing appendices...</p>
            <p className="text-slate-300 text-sm mt-1">This may take a moment</p>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-4">
            <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
              <p className="text-emerald-300 font-bold text-lg">{result.created.length} books created!</p>
              {result.cross_references_linked > 0 && (
                <p className="text-emerald-400 text-sm mt-1">{result.cross_references_linked} cross-references auto-linked</p>
              )}
              {result.ai_summaries_generated > 0 && (
                <p className="text-purple-400 text-sm mt-1">{result.ai_summaries_generated} AI summaries generated</p>
              )}
            </div>

            {result.created.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-1" data-testid="split-result-list">
                {result.created.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 rounded bg-slate-800 text-sm text-slate-300">
                    <span className="font-mono text-emerald-400 w-8">{b.appendix_code}</span>
                    <span className="flex-1 truncate">{b.title}</span>
                    <span className="text-xs text-slate-300">{(b.content_length / 1000).toFixed(1)}k chars</span>
                  </div>
                ))}
              </div>
            )}

            {result.skipped.length > 0 && (
              <details className="text-sm">
                <summary className="text-slate-300 cursor-pointer hover:text-slate-200">
                  {result.skipped.length} sections skipped
                </summary>
                <div className="mt-1 space-y-1">
                  {result.skipped.map((s, i) => (
                    <div key={i} className="px-3 py-1 text-slate-300">{s.title} — {s.reason}</div>
                  ))}
                </div>
              </details>
            )}

            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={onComplete} data-testid="split-done-btn">
              Done — Refresh Library
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
